import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { distinctUntilChanged, skip } from 'rxjs/operators';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../core/_base/crud';
import {
  ClearContactRelationshipState,
  ContactRelationshipActionTypes,
  ContactRelationshipModel,
  ContactRelationshipOnServerCreated,
  ContactRelationshipOnServerUpdated,
  ContactRelationshipsDataSource,
  ContactRelationshipsPageRequested,
  OneContactRelationshipOnServerDeleted,
  selectContactRelationshipErrorState,
  selectContactRelationshipLatestSuccessfullAction,
  selectContactRelationshipsActionLoading,
  ViewContext,
} from '../../../../../../../core/lists';
import { EntityRelationshipEditComponent } from '../relationship-edit/entity-relationship-edit.component';

import { TranslateService } from '@ngx-translate/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ApiActionTypes } from '../../../../../../../core/events';
import { selectContactRelationshipsPageLastQuery } from '../../../../../../../core/lists/_selectors/contact-relationship.selectors';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../../../../../../core/auth/_services';
import { of } from 'rxjs';
import { GlobalSettingsService } from '../../../../../../../core/services/global-settings.service';
import { CreateFilteredViewDialogComponent } from '../../../../../filtered-view/create-filtered-view-dialog/create-filtered-view-dialog.component';
import { GeneralValidationMessagesService } from '../../../../../../../core/services/general-validation-messages.service';
import { NavigationService } from '../../../../../../../core/services/navigation.service';
import { Update } from '@ngrx/entity';

export enum RelationClassification {
  FINANCIAL_RESPONSIBLE = 'FINANCIAL_RESPONSIBLE',
  EMERGENCY_CONTACT = 'EMERGENCY_CONTACT',
  PHI_AUTHORIZED = 'PHI_AUTHORIZED',
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-entity-relationships-list',
  templateUrl: './entity-relationships-list.component.html',
  styleUrls: ['./entity-relationships-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EntityRelationshipsListComponent implements OnInit, OnDestroy {
  entity$: any;
  entityType: any;
  @Output() openList = new EventEmitter<any>();
  @Output() navigateToContact = new EventEmitter<any>();
  lastQuery: QueryParamsModel;
  viewLoading = false;

  // Table fields
  dataSource: ContactRelationshipsDataSource;
  displayedColumns = ['name', 'relation_type_id', 'description', 'classification', 'actions'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  // Selection
  selection = new SelectionModel<ContactRelationshipModel>(true, []);
  contactRelationshipsResult: ContactRelationshipModel[] = [];
  // Edit
  formGroup: FormGroup;
  relForEdit: ContactRelationshipModel;
  // Error Handling
  submissionErrorObj: any;
  isViewMode: boolean;
  loading = true;
  // Private properties
  private subscriptions: any[] = [];
  isFilteredView$ = of(false);
  isFilteredView = false;
  @Input() entity_id;
  @Input() entity_type;
  constructor(
    private navigationService: NavigationService,
    private globalService: GeneralValidationMessagesService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    public globalSettings: GlobalSettingsService,
    private store: Store<AppState>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private translate: TranslateService,
    private layoutUtilsService: LayoutUtilsService,
    private router: Router,
    private activateRoute: ActivatedRoute,
  ) {}

  ngOnInit() {
    const errorSubscription = this.store
      .pipe(select(selectContactRelationshipErrorState), skip(1))
      .subscribe((res) => {
        if (res) {
          this.layoutUtilsService.showActionNotification(
            this.translate.instant('GLOBAL.UNSPECIFIED_ERROR'),
            MessageType.Delete,
            3000,
            true,
            false,
          );
        }
      });
    this.subscriptions.push(errorSubscription);

    const successSubscription = this.store
      .pipe(select(selectContactRelationshipLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          let successMsg;
          let msgType;
          switch (res.action_type) {
            case ContactRelationshipActionTypes.ContactRelationshipCreated:
              successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
              msgType = MessageType.Create;
              break;
            case ApiActionTypes.ApiUpdated:
              successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.UPDATE_MESSAGE');
              msgType = MessageType.Update;
              break;
          }
          this.layoutUtilsService.showActionNotification(successMsg, msgType, 3000, true, false);
        }
      });
    this.subscriptions.push(successSubscription);
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push(sortSubscription);
    // Init DataSource
    this.dataSource = new ContactRelationshipsDataSource(this.store);
    const entitiesSubscription = this.dataSource.entitySubject
      .pipe(skip(1), distinctUntilChanged())
      .subscribe((res) => {
        this.contactRelationshipsResult = res;
        this.loading = false;
      });
    this.subscriptions.push(entitiesSubscription);
    const lastQuerySubscription = this.store
      .pipe(select(selectContactRelationshipsPageLastQuery))
      .subscribe((res) => (this.lastQuery = res));
    // Load last query from store
    this.subscriptions.push(lastQuerySubscription);

    const loadingSub = this.store
      .pipe(select(selectContactRelationshipsActionLoading))
      .subscribe((res) => (this.viewLoading = res));
    this.subscriptions.push(loadingSub);
    if (this.activateRoute.url[`_value`][1]?.path === 'view') {
      this.isViewMode = true;
    }
    // tslint:disable-next-line:max-line-length
    switch (this.entity_type) {
      case 'doctors':
        this.entityType = 'DOCTOR';
        break;
      case 'patients':
        this.entityType = 'PATIENT';
        break;
      case 'facilities':
        this.entityType = 'FACILITY';
        break;
      case 'clients':
        this.entityType = 'CLIENT';
        break;
    }
    if (!this.entity_id) {
      this.activateRoute.params.subscribe((params) => {
        this.entity_id = params.id;
      });
    }
    this.globalService.getEntitiesById(this.entity_type, this.entity_id).subscribe((res) => {
      if (res) {
        this.entity$ = res.data;
        this.loadRelsList();
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
    this.store.dispatch(new ClearContactRelationshipState());
  }

  loadRelsList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      {},
      this.sort.direction,
      this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(
      new ContactRelationshipsPageRequested({
        page: queryParams,
        contactId: this.entity$.id,
        type: this.entityType.toLowerCase(),
      }),
    );
  }

  deleteRel(_item: ContactRelationshipModel) {
    const _title = 'Relationship Delete';
    const _description = 'Are you sure to permanently delete this relationship?';
    const _waitDesciption = 'Relationship is deleting...';
    const _deleteMessage = `Relationship has been deleted`;

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      const payload: any = {};
      payload.contactRelationship = _item;
      if (this.entityType === 'DOCTOR') {
        payload.doctor = this.entity$;
      }
      if (this.entityType === 'CLIENT') {
        payload.client = this.entity$;
      }
      if (this.entityType === 'FACILITY') {
        payload.facility = this.entity$;
      }
      if (this.entityType === 'PATIENT') {
        payload.patient = this.entity$;
      }
      this.store.dispatch(new OneContactRelationshipOnServerDeleted(payload));
      this.loadRelsList();
      this.layoutUtilsService.showActionNotification(
        _deleteMessage,
        MessageType.Delete,
        3000,
        true,
        false,
      );
    });
  }

  editContact(rel) {
    const queryParams = {
      viewContext: ViewContext.contacts,
      entityType: this.entityType,
      id: this.activateRoute.snapshot.paramMap.get('id'),
      entity_id: this.activateRoute.snapshot.paramMap.get('id'),
    };
    this.navigationService.navigateTo('lists/contacts/edit/' + rel.contact.id, queryParams);
    this.navigateToContact.emit(true);
  }

  addRel(relation?: any) {
    const newRel = new ContactRelationshipModel();
    newRel.clear();
    const dialogRef = this.dialog.open(EntityRelationshipEditComponent, {
      data: {
        contactEntity: false,
        entity_type: this.entityType,
        entity_object: relation,
        entity_id: this.entity$.id,
      },
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res && res.done) {
        newRel.id = relation?.id;
        newRel.entity_id = this.entity$.id;
        newRel.entity_type = this.entityType;
        newRel.title = res.response.title;
        newRel.relation_type = res.response.relation_type;
        newRel.relation_type_id = res.response.relation_type_id;
        newRel.relationship_description = res.response.relationship_description;
        newRel.relation_classification = res.response.relation_classification;
        newRel.is_prime = res.response.is_prime;
        newRel.emergency_contact = res.response.emergency_contact;
        newRel.financial_responsible = res.response.financial_responsible;
        newRel.phi_authorized = res.response.phi_authorized;
        newRel.valid_until_revoked = res.response.valid_until_revoked;
        newRel.authorized_upto_date = res.response.authorized_upto_date;
        if (relation) {
          const payload: any = {};
          payload.contactRelationship = relation;
          if (this.entityType === 'DOCTOR') {
            payload.doctor = this.entity$;
          }
          if (this.entityType === 'CLIENT') {
            payload.client = this.entity$;
          }
          if (this.entityType === 'FACILITY') {
            payload.facility = this.entity$;
          }
          if (this.entityType === 'PATIENT') {
            payload.patient = this.entity$;
          }
        }
        this.store.dispatch(
          new ContactRelationshipOnServerCreated({
            contactRelationship: newRel,
            contact_id: res.response.entity_id,
            type: this.entityType.toLowerCase(),
          }),
        );
        this.loadRelsList();
        let successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
        if (relation) {
          successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.UPDATE_MESSAGE');
        }
        this.layoutUtilsService.showActionNotification(
          successMsg,
          MessageType.Create,
          3000,
          true,
          false,
        );
      }
    });
  }

  getFullName(obj) {
    if (obj && obj.contact) return obj.contact.first_name + ' ' + obj.contact.last_name;
  }

  editRelationshipButtonOnClick(relation?: any) {
    const dialogRef = this.dialog.open(EntityRelationshipEditComponent, {
      data: {
        contactEntity: false,
        entity_type: this.entityType,
        entity_object: relation,
        entity_id: this.entity$.id,
      },
      width: '800px',
    });
    dialogRef.afterClosed().subscribe((res) => {
      const newRel = new ContactRelationshipModel();
      newRel.clear();
      if (res && res.done) {
        newRel.id = relation.id;
        newRel.entity_id = this.entity$.id;
        newRel.entity_type = this.entityType;
        newRel.contact_id = res.response.entity_id;
        newRel.title = res.response.title;
        newRel.relation_type = res.response.relation_type;
        newRel.relation_type_id = res.response.relation_type_id;
        newRel.relationship_description = res.response.relationship_description;
        newRel.relation_classification = res.response.relation_classification;
        newRel.is_prime = res.response.is_prime;
        newRel.emergency_contact = res.response.emergency_contact;
        newRel.financial_responsible = res.response.financial_responsible;
        newRel.phi_authorized = res.response.phi_authorized;
        newRel.valid_until_revoked = res.response.valid_until_revoked;
        newRel.authorized_upto_date = res.response.authorized_upto_date;
        if (relation) {
          const payload: any = {};
          payload.contactRelationship = relation;
          if (this.entityType === 'DOCTOR') {
            payload.doctor = this.entity$;
          }
          if (this.entityType === 'CLIENT') {
            payload.client = this.entity$;
          }
          if (this.entityType === 'FACILITY') {
            payload.facility = this.entity$;
          }
          if (this.entityType === 'PATIENT') {
            payload.patient = this.entity$;
          }
        }
        const updateRelation: Update<ContactRelationshipModel> = {
          id: newRel.id,
          changes: newRel,
        };
        this.store.dispatch(
          new ContactRelationshipOnServerUpdated({
            partialContactRelationship: updateRelation,
            contactRelationship: newRel,
          }),
        );
        this.loadRelsList();
        let successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.ADD_MESSAGE');
        if (relation) {
          successMsg = this.translate.instant('CONTACT_RELATIONSHIP.EDIT.UPDATE_MESSAGE');
        }
        this.layoutUtilsService.showActionNotification(
          successMsg,
          MessageType.Create,
          3000,
          true,
          false,
        );
      }
    });
  }
  openFilteredViewDialog(val) {
    if (this.authService.getChange() && !window.confirm('Changes you made may not be saved.')) {
      return of(false);
    }
    this.isFilteredView = this.globalSettings.isFilteredView();
    const dialogRef = this.dialog.open(CreateFilteredViewDialogComponent, {
      width: '800px',
      data: {
        sessionOpened: !!this.isFilteredView,
        entity: val.contact,
        type: this.entityType,
      },
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(() => {
      setTimeout(() => {
        this.isFilteredView = this.globalSettings.isFilteredView();
        if (this.isFilteredView) {
          this.openList.emit(true);
        }
        this.cdr.detectChanges();
      }, 30);
    });
  }
}
